import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useMemo, useRef, useState } from 'react';
import { TableHeadingCell, TableHead, TableCell, TableRow, TableBody as TableBody$2, ResponsiveTable, Table, PPTableContext, TableFoot } from '@paypalcorp/pp-react-table';
import { Button, ButtonGroup } from '@paypalcorp/pp-react-buttons';
import { devWarning } from '@paypalcorp/pp-react-utils';
import { SettingsIcon, ArrowUpSmIcon, ArrowDownSmIcon, RefreshIcon, Icon, ChevronUpIcon, ChevronDownIcon } from '@paypalcorp/pp-react-icons';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { Checkbox } from '@paypalcorp/pp-react-checkbox';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { stripUnit, remToPx } from 'polished';
import { ActionMenu } from '@paypalcorp/pp-react-menu';
import _typeof from '@babel/runtime/helpers/esm/typeof';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16;

function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceXs = tokens.sysSpaceXs,
      sysSizePictogramSm = tokens.sysSizePictogramSm,
      sysSizeCircleLg = tokens.sysSizeCircleLg,
      sysSizeMediaMd = tokens.sysSizeMediaMd,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
      sysColorPrimaryMain = tokens.sysColorPrimaryMain,
      sysColorStructureScrim = tokens.sysColorStructureScrim;
  var mdBreakpoint = stripUnit(remToPx(sysSizeMediaMd));
  var flexContainer = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    display: flex;\n    align-items: center;\n  "])));
  var settingsIcon = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    display: grid;\n    grid-template-columns: 1fr auto;\n    gap: ", ";\n    align-items: center;\n  "])), sysSpaceMd);
  var interactiveCellCheckbox = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label {\n      display: block;\n    }\n  "])));
  var interactiveCellCheckboxDisabled = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: interactive_cell_checkbox_disabled;\n    visibility: hidden;\n  "])));
  var emptyTableLabel = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: empty_table_label;\n    background-size: ", ";\n  "])), sysSizeCircleLg);
  var infoBlock = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: info_block;\n    margin-top: ", ";\n    display: grid;\n    grid-template-columns: auto;\n    grid-template-rows: auto auto;\n    place-items: center;\n  "])), sysSpaceMd);
  var infoBlockMessage = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: iblock_msg;\n    margin: ", " 0;\n  "])), sysSpaceMd);
  var infoBlockAction = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: iblock_action;\n    margin: ", " 0;\n  "])), sysSpaceMd);
  var infoBlockDecoration = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: iblock_deco;\n    width: ", ";\n  "])), sysSizePictogramSm);
  var iconMessage = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: icon_message;\n    padding: ", ";\n    display: inline-grid;\n    grid-template-columns: auto 1fr;\n    place-items: center;\n    gap: ", ";\n    height: auto;\n  "])), sysSpaceSm, sysSpaceSm);
  var seeMoreBlock = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: see_more;\n    padding: ", " ", ";\n  "])), sysSpaceSm, sysSpaceMd);
  var headerButton = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: header_button;\n    margin-left: ", ";\n\n    [dir='rtl'] & {\n      margin-right: ", ";\n      margin-left: 0;\n    }\n  "])), sysSpaceXs, sysSpaceXs);
  var currentlySortedByIcon = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: currently_sorted_by_icon;\n    color: ", ";\n    border: ", " solid ", ";\n    border-radius: ", ";\n  "])), sysColorPrimaryMain, sysBorderThicknessNormal, sysColorPrimaryMain, sysBorderRadiusInteractive);
  var tableLoading = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: data_table_loading;\n    position: relative;\n\n    &:before {\n      content: ' ';\n      position: absolute;\n      inset: 0 0 0 0;\n      background-color: ", ";\n      z-index: 1;\n    }\n  "])), sysColorStructureScrim);
  var spinnerBlock = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: data_table_spinner;\n    padding: ", ";\n    display: grid;\n    place-items: center;\n  "])), sysSpaceMd);
  var spinnerOverlay = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: data_table_spinner;\n    position: absolute;\n    top: 50%;\n    left: 50%;\n    transform: translate(-50%, -50%);\n    z-index: 1;\n  "])));
  return {
    mdBreakpoint: mdBreakpoint,
    flexContainer: flexContainer,
    settingsIcon: settingsIcon,
    seeMoreBlock: seeMoreBlock,
    interactiveCellCheckbox: interactiveCellCheckbox,
    interactiveCellCheckboxDisabled: interactiveCellCheckboxDisabled,
    emptyTableLabel: emptyTableLabel,
    headerButton: headerButton,
    tableLoading: tableLoading,
    spinnerBlock: spinnerBlock,
    spinnerOverlay: spinnerOverlay,
    iconMessage: iconMessage,
    infoBlock: infoBlock,
    infoBlockMessage: infoBlockMessage,
    infoBlockAction: infoBlockAction,
    infoBlockDecoration: infoBlockDecoration,
    currentlySortedByIcon: currentlySortedByIcon
  };
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}
/*
 * We're now assuming that columnOrder exists and includes only the names
 * of columns which are visible. This has been taken care of in dataTable.tsx.
 */


var TableHeader = function TableHeader(_ref) {
  var columns = _ref.columns,
      columnOrder = _ref.columnOrder,
      isInteractive = _ref.isInteractive,
      _ref$toggleAllLabel = _ref.toggleAllLabel,
      toggleAllLabel = _ref$toggleAllLabel === void 0 ? 'Toggle all rows' : _ref$toggleAllLabel,
      selectedRows = _ref.selectedRows,
      idPrefix = _ref.idPrefix,
      rows = _ref.rows,
      interactiveCellContentClassName = _ref.interactiveCellContentClassName,
      onSelection = _ref.onSelection,
      onSort = _ref.onSort,
      sortInProgress = _ref.sortInProgress,
      settings = _ref.settings,
      tableContext = _ref.tableContext;
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var rowIds = useMemo(function () {
    return rows.reduce(function (acc, _ref2) {
      var id = _ref2.id,
          isUnselectable = _ref2.isUnselectable;

      if (!isUnselectable) {
        acc.push(id);
      }

      return acc;
    }, []);
  }, [rows]);
  var checkboxElm;
  var checkboxElmCSSVal = [_styles.flexContainer, rowIds.length === 0 && _styles.interactiveCellCheckboxDisabled];

  if (isInteractive) {
    var isAnythingChecked = selectedRows.length > 0;
    var allChecked = rowIds.length && selectedRows.length === rowIds.length;
    checkboxElm = jsx(Checkbox, {
      name: "".concat(idPrefix, "DataTableToggleAll"),
      id: "".concat(idPrefix, "DataTableToggleAll"),
      "aria-label": toggleAllLabel,
      css: checkboxElmCSSVal,
      partial: isAnythingChecked && !allChecked,
      checked: isAnythingChecked || allChecked,
      onChange: function handleChange(evt) {
        onSelection(evt.target.checked ? rowIds : []);
      },
      "data-ppui": "true"
    });
  }

  var renderInteractiveCellContent = function renderInteractiveCellContent(content) {
    return jsx("div", {
      css: _styles.flexContainer,
      className: interactiveCellContentClassName,
      "data-ppui": "true"
    }, checkboxElm, content);
  };

  var hasContent = function hasContent(title) {
    return typeof title === 'string' && title.length > 0 || !!title;
  };

  var renderColumnHeader = function renderColumnHeader(name, _ref3, index, isLast) {
    var title = _ref3.title,
        sortButtonLabel = _ref3.sortButtonLabel,
        sortDirection = _ref3.sortDirection,
        isSortCurrent = _ref3.isSortCurrent,
        align = _ref3.align,
        width = _ref3.width,
        claimsRemainingSpace = _ref3.claimsRemainingSpace;
    var contents = title;

    var handleClick = function handleClick() {
      /* istanbul ignore next */
      if (onSort) {
        onSort(name, sortDirection === 'ascending' ? 'descending' : 'ascending');
      }
    };

    var settingsClick = function settingsClick() {
      /* istanbul ignore next */
      if (settings && settings.onClick) {
        settings.onClick();
      }
    };

    if (sortDirection) {
      var SortIcon = sortDirection === 'ascending' ? ArrowUpSmIcon : ArrowDownSmIcon;
      var labelPrefix = isSortCurrent ? 'Currently' : '';
      var hiddenLabel = sortButtonLabel || "".concat(labelPrefix, " sorted by ").concat(name, " in ").concat(sortDirection, " order");
      contents = jsx("div", {
        css: _styles.flexContainer,
        "data-ppui": "true"
      }, title, jsx(ClassNames, {
        "data-ppui": "true"
      }, function (_ref4) {
        var css = _ref4.css;
        return jsx(IconButton, {
          css: _styles.headerButton,
          iconClassName: isSortCurrent && css(_styles.currentlySortedByIcon),
          svgIconComponent: SortIcon,
          onClick: handleClick,
          buttonType: "tertiary",
          btnState: sortInProgress === name ? 'processing' : 'initial',
          size: "sm",
          "data-ppui": "true"
        }, hiddenLabel);
      }));
    }

    if (index === 0 && isInteractive) {
      contents = renderInteractiveCellContent(contents);
    }

    if (isLast && typeof (settings === null || settings === void 0 ? void 0 : settings.onClick) === 'function') {
      var icon = jsx(IconButton, {
        svgIconComponent: SettingsIcon,
        buttonType: "tertiary",
        onClick: settingsClick,
        "data-ppui": "true"
      }, settings.screenReaderText);

      if (hasContent(title)) {
        contents = jsx("div", {
          css: _styles.settingsIcon,
          "data-ppui": "true"
        }, jsx("div", {
          "data-ppui": "true"
        }, contents), jsx("div", {
          "data-ppui": "true"
        }, icon));
      } else {
        var _align;

        contents = icon;
        align = (_align = align) !== null && _align !== void 0 ? _align : 'end';
      }
    }

    var cellContext = _objectSpread(_objectSpread({}, tableContext), {}, {
      section: 'thead',
      isFirst: index === 0,
      isSticky: true
    });

    return jsx(TableHeadingCell, {
      key: name,
      scope: "col",
      "aria-sort": sortDirection,
      align: align,
      width: width,
      tableContext: cellContext,
      claimsRemainingSpace: claimsRemainingSpace,
      "data-ppui": "true"
    }, contents);
  };

  var lastIndex = columnOrder.length - 1;
  var headerElms = columnOrder.map(function (name, index) {
    return renderColumnHeader(name, columns[name], index, index === lastIndex);
  });
  return jsx(TableHead, {
    sticky: true,
    "data-ppui": "true"
  }, jsx("tr", {
    "data-ppui": "true"
  }, headerElms));
};

var TableHeader$1 = React.memo(TableHeader);

function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}
/*
 * We're now assuming that columnOrder exists and includes only the names
 * of columns which are visible. This has been taken care of in dataTable.tsx.
 */


var TableBody = function TableBody(_ref) {
  var tableContext = _ref.tableContext,
      rows = _ref.rows,
      isInteractive = _ref.isInteractive,
      idPrefix = _ref.idPrefix,
      selectedRows = _ref.selectedRows,
      columns = _ref.columns,
      columnOrder = _ref.columnOrder,
      interactiveCellContentClassName = _ref.interactiveCellContentClassName,
      onSelection = _ref.onSelection,
      loadingProps = _ref.loadingProps;
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var renderCellContent = function renderCellContent(row, columnName) {
    var content = row[columnName] ? row[columnName].value : '';

    if (columns[columnName].cellAs) {
      var Component = columns[columnName].cellAs;
      content = jsx(Component, _extends({
        id: row.id
      }, row[columnName], {
        "data-ppui": "true"
      }));
    }

    return content;
  };

  var renderInteractiveCellContent = function renderInteractiveCellContent(checkboxElm, content) {
    return jsx("div", {
      css: _styles.flexContainer,
      className: interactiveCellContentClassName,
      "data-ppui": "true"
    }, checkboxElm, content);
  };

  var renderCells = function renderCells(row, checkboxElm) {
    return columnOrder.map(function (columnName, index) {
      var content = renderCellContent(row, columnName);
      var _columns$columnName = columns[columnName],
          mobile = _columns$columnName.mobile,
          align = _columns$columnName.align,
          width = _columns$columnName.width;

      var cellContext = _objectSpread$1(_objectSpread$1({}, tableContext), {}, {
        section: 'tbody'
      });

      if (index === 0 && isInteractive) {
        cellContext.isFirst = true;
        return jsx(TableHeadingCell, {
          tableContext: cellContext,
          key: columnName,
          mobile: mobile,
          align: align,
          width: width,
          scope: "row",
          "data-ppui": "true"
        }, renderInteractiveCellContent(checkboxElm, content));
      }

      return jsx(TableCell, {
        tableContext: cellContext,
        key: columnName,
        mobile: mobile,
        align: align,
        width: width,
        "data-ppui": "true"
      }, content);
    });
  };

  var rowElms = rows.map(function (row) {
    var key = row.id;
    var checkboxElm;

    if (isInteractive) {
      checkboxElm = jsx(Checkbox, {
        name: "".concat(idPrefix, "DataTable").concat(key),
        id: "".concat(idPrefix, "DataTable").concat(key),
        "aria-label": key,
        css: [_styles.interactiveCellCheckbox, row.isUnselectable && _styles.interactiveCellCheckboxDisabled],
        disabled: !!row.isUnselectable,
        checked: selectedRows.includes(key),
        onChange: function handleChange(evt) {
          var updatedSelection = evt.target.checked ? selectedRows.concat(key) : selectedRows.filter(function (item) {
            return item !== key;
          });
          onSelection(updatedSelection);
        },
        "data-ppui": "true"
      });
    }

    var isSelected = isInteractive && selectedRows.includes(key);
    var cellElms = renderCells(row, checkboxElm);
    return jsx(TableRow, {
      key: key,
      isSelected: isSelected,
      tableContext: {
        section: 'tbody'
      },
      "data-ppui": "true"
    }, cellElms);
  });

  var EmptyBlock = function EmptyBlock(_ref2) {
    var decoration = _ref2.decoration,
        buttonLabel = _ref2.buttonLabel,
        buttonRef = _ref2.buttonRef,
        message = _ref2.message,
        isError = _ref2.isError,
        buttonAction = _ref2.buttonAction,
        processing = _ref2.processing;
    var tableStyles = _styles;
    /* istanbul ignore next */

    var button = buttonLabel && buttonAction ? jsx(Button, {
      onClick: buttonAction,
      secondary: isError,
      tertiary: !isError,
      size: "sm",
      ref: buttonRef,
      key: "load-btn",
      btnState: processing ? 'processing' : 'initial',
      "data-ppui": "true"
    }, buttonLabel) : null;
    return jsx("div", {
      css: tableStyles.infoBlock,
      "data-ppui": "true"
    }, decoration, jsx("div", {
      css: tableStyles.infoBlockMessage,
      "data-ppui": "true"
    }, message), jsx("div", {
      css: tableStyles.infoBlockAction,
      "data-ppui": "true"
    }, button));
  };

  if (rowElms.length) {
    return jsx(TableBody$2, {
      "data-ppui": "true"
    }, rowElms);
  }

  var contents;

  if (loadingProps.errorMsg) {
    contents = jsx(EmptyBlock, {
      decoration: jsx(RefreshIcon, {
        color: "PrimaryMain",
        size: "xl",
        "data-ppui": "true"
      }),
      buttonLabel: loadingProps.retryButtonLabel,
      buttonAction: loadingProps.onRetry,
      buttonRef: loadingProps.retryButtonRef,
      isError: true,
      message: loadingProps.errorMsg,
      processing: loadingProps.isLoading,
      "data-ppui": "true"
    });
  } else if (loadingProps.isLoading) {
    contents = jsx("div", {
      css: _styles.spinnerBlock,
      "data-ppui": "true"
    }, jsx(LoadingSpinner, {
      size: "lg",
      as: "div",
      screenReaderText: loadingProps.loadingText,
      "aria-live": loadingProps.loadingText,
      "data-ppui": "true"
    }));
  } else {
    contents = jsx(EmptyBlock, {
      decoration: jsx("img", {
        css: _styles.infoBlockDecoration,
        alt: "emtpy",
        src: "https://www.paypalobjects.com/paypal-ui/pictograms/grayscale/svg/grayscale-1.svg",
        "data-ppui": "true"
      }),
      buttonLabel: loadingProps.retryButtonLabel,
      buttonAction: loadingProps.onRetry,
      buttonRef: loadingProps.retryButtonRef,
      isError: false,
      message: loadingProps.emptyMsg,
      "data-ppui": "true"
    });
  }

  return jsx(TableBody$2, {
    "data-ppui": "true"
  }, jsx(TableRow, {
    "data-ppui": "true"
  }, jsx(TableCell, {
    span: columnOrder.length,
    align: "center",
    css: _styles.emptyTableLabel,
    mobile: {
      placement: 'ab123'
    },
    "data-ppui": "true"
  }, contents)));
};

var TableBody$1 = React.memo(TableBody);
var _excluded = ["rows", "columns", "columnOrder", "description", "className", "isLoading", "loadingStateLabel", "isInteractive", "selectedRows", "toggleAllLabel", "emptyTableLabel", "loadingProps", "idPrefix", "interactiveCellContentClassName", "onSelection", "onSort", "sortInProgress", "settings", "seeMoreProps"];

function ownKeys$2(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread$2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var isResponsive = function isResponsive(cols) {
  return Object.entries(cols).findIndex(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        _key = _ref2[0],
        value = _ref2[1];

    return value.hasOwnProperty('mobile');
  }) >= 0;
}; // istanbul ignore next


var deprecationWarning = function deprecationWarning(propName) {
  devWarning("Deprecated: the prop ".concat(propName, " has been deprecated and will have no effect."));
};

var deprecationCheck = function deprecationCheck(deprecatedItems, props) {
  deprecatedItems.forEach(function (item) {
    // istanbul ignore next
    if (props.hasOwnProperty(item)) {
      deprecationWarning(item);
    }
  });
};

var onlyVisible = function onlyVisible(cols, order) {
  var keyList = !order || order.length === 0 ? Object.keys(cols) : order;
  return keyList.filter(function (col) {
    var _cols$col;

    return ((_cols$col = cols[col]) === null || _cols$col === void 0 ? void 0 : _cols$col.isVisible) !== false;
  });
};

var DataTable = function DataTable(_ref3) {
  var _ref3$rows = _ref3.rows,
      rows = _ref3$rows === void 0 ? [] : _ref3$rows,
      _ref3$columns = _ref3.columns,
      columns = _ref3$columns === void 0 ? {} : _ref3$columns,
      columnOrder = _ref3.columnOrder,
      _ref3$description = _ref3.description,
      description = _ref3$description === void 0 ? 'Table' : _ref3$description,
      className = _ref3.className,
      _ref3$isLoading = _ref3.isLoading,
      isLoading = _ref3$isLoading === void 0 ? false : _ref3$isLoading,
      _ref3$loadingStateLab = _ref3.loadingStateLabel,
      loadingStateLabel = _ref3$loadingStateLab === void 0 ? 'Loading data' : _ref3$loadingStateLab,
      _ref3$isInteractive = _ref3.isInteractive,
      isInteractive = _ref3$isInteractive === void 0 ? false : _ref3$isInteractive,
      _ref3$selectedRows = _ref3.selectedRows,
      selectedRows = _ref3$selectedRows === void 0 ? [] : _ref3$selectedRows,
      toggleAllLabel = _ref3.toggleAllLabel,
      emptyTableLabel = _ref3.emptyTableLabel,
      loadingProps = _ref3.loadingProps,
      idPrefix = _ref3.idPrefix,
      interactiveCellContentClassName = _ref3.interactiveCellContentClassName,
      onSelection = _ref3.onSelection,
      onSort = _ref3.onSort,
      sortInProgress = _ref3.sortInProgress,
      settings = _ref3.settings,
      seeMoreProps = _ref3.seeMoreProps,
      rest = _objectWithoutProperties(_ref3, _excluded);

  if (!rows.length) {
    console.warn('Required prop `rows` is missing.');
  }
  /* istanbul ignore next */


  if (!Object.keys(columns).length) {
    console.warn('Required prop `columns` is missing.');
  }

  var tokens = useTheme();
  var tableStyles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var visibleCols = onlyVisible(columns, columnOrder);
  var wrapperRef = useRef(null);
  deprecationCheck(['combinedColCellContentClassName', 'isHeaderCompact', 'isBodyCompact'], rest);
  var spinnerElm = isLoading && jsx("div", {
    css: tableStyles.spinnerOverlay,
    "data-ppui": "true"
  }, jsx(LoadingSpinner, {
    screenReaderText: loadingStateLabel,
    size: "md",
    "data-ppui": "true"
  }));
  var props = {
    columns: columns,
    columnOrder: visibleCols,
    rows: rows,
    isInteractive: isInteractive,
    idPrefix: idPrefix,
    selectedRows: selectedRows,
    interactiveCellContentClassName: interactiveCellContentClassName,
    onSelection: onSelection
  };
  var TableComponent = isResponsive(columns) ? ResponsiveTable : Table;
  var tableContext = {
    tableType: 'data',
    isResponsive: isResponsive(columns)
  };
  return jsx("div", {
    css: isLoading && tableStyles.tableLoading,
    className: className,
    "data-ppui-info": "data-table_2.12.0",
    ref: wrapperRef
  }, spinnerElm, jsx(TableComponent, _extends({
    isLegacy: false,
    "aria-label": description
  }, rest, {
    "data-ppui": "true"
  }), jsx(PPTableContext.Provider, {
    value: _objectSpread$2(_objectSpread$2({}, tableContext), {}, {
      section: 'thead'
    }),
    "data-ppui": "true"
  }, jsx(TableHeader$1, _extends({}, props, {
    toggleAllLabel: toggleAllLabel,
    onSort: onSort,
    sortInProgress: sortInProgress,
    settings: settings,
    "data-ppui": "true"
  }))), jsx(PPTableContext.Provider, {
    value: _objectSpread$2(_objectSpread$2({}, tableContext), {}, {
      section: 'tbody'
    }),
    "data-ppui": "true"
  }, jsx(TableBody$1, _extends({}, props, {
    loadingProps: loadingProps !== null && loadingProps !== void 0 ? loadingProps : {
      emptyMsg: emptyTableLabel,
      loadingText: loadingStateLabel
    },
    "data-ppui": "true"
  }))), jsx(function DataTableFooter(_ref4) {
    var onClick = _ref4.onClick,
        buttonLabel = _ref4.buttonLabel,
        buttonRef = _ref4.buttonRef,
        processing = _ref4.isLoading,
        errorMsg = _ref4.errorMsg,
        colCount = _ref4.colCount,
        backToTopLabel = _ref4.backToTopLabel,
        onBackToTopClick = _ref4.onBackToTopClick;
    var errorRow = errorMsg ? jsx(TableRow, {
      "data-ppui": "true"
    }, jsx(TableCell, {
      span: colCount,
      align: "center",
      tableContext: {
        section: 'utility'
      },
      mobile: {
        placement: 'ab123'
      },
      "data-ppui": "true"
    }, jsx("div", {
      css: tableStyles.iconMessage,
      "data-ppui": "true"
    }, jsx(Icon, {
      name: "refresh",
      size: "sm",
      color: "PrimaryMain",
      "data-ppui": "true"
    }), jsx("div", {
      "data-ppui": "true"
    }, errorMsg)))) : null;

    var isVisible = function isVisible(elem) {
      return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
    };

    var seeMoreButton = onClick ? jsx(Button, {
      onClick: onClick,
      secondary: true,
      size: "sm",
      key: "see-more-button",
      ref: buttonRef,
      btnState: processing ? 'processing' : 'initial',
      "data-ppui": "true"
    }, buttonLabel) : null;
    var backToTopButton = onBackToTopClick ? jsx(Button, {
      onClick: function handleBackToTop(evt) {
        var target = evt.target;
        var table = target.closest('table'); // istanbul ignore next

        if (table) {
          // We want to find the first visible element in the table
          // make it focusable, set focus to it and then make it not focusable
          // Usually, that's the first th in the header.
          // BUT... the header is hidden for responsive tables. So we have
          // to find the first child (may be th or td) of the first row
          // of the body.     --love, BCC
          var firstCell = table.querySelector('thead>tr>th');

          if (!isVisible(firstCell)) {
            var firstRow = table.querySelector('tbody>tr');
            firstCell = firstRow.firstChild;
          }

          firstCell.tabIndex = 1;
          firstCell.focus();
          requestAnimationFrame(function () {
            firstCell.tabIndex = -1;
          });
          table.scrollTop = 0;
        } // istanbul ignore next


        if (typeof onBackToTopClick === 'function') {
          onBackToTopClick();
        }
      },
      secondary: true,
      size: "sm",
      iconComponent: ArrowUpSmIcon,
      "data-ppui": "true"
    }, backToTopLabel) : null;
    return seeMoreButton || backToTopButton ? jsx(TableFoot, {
      "data-ppui": "true"
    }, errorRow, jsx(TableRow, {
      "data-ppui": "true"
    }, jsx(TableCell, {
      span: colCount,
      align: "center",
      mobile: {
        placement: 'ab123'
      },
      "data-ppui": "true"
    }, jsx(ButtonGroup, {
      "data-ppui": "true"
    }, backToTopButton, seeMoreButton)))) : null;
  }, _extends({}, seeMoreProps, {
    colCount: visibleCols.length,
    "data-ppui": "true"
  }))));
};

var doCompare = function doCompare(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
};

var ASCENDING = 1;
var DESCENDING = -1;
var DataTableUtils = {
  sortCompare: function sortCompare(a, b, columnName) {
    var direction = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'ascending';
    var dir = direction === 'ascending' ? ASCENDING : DESCENDING;
    return doCompare(a[columnName].value, b[columnName].value) * dir;
  }
};

var DataTableSortMenu = function DataTableSortMenu(_ref) {
  var selectedIndex = _ref.selectedIndex,
      options = _ref.options,
      _ref$triggerLabel = _ref.triggerLabel,
      triggerLabel = _ref$triggerLabel === void 0 ? 'Open sort menu' : _ref$triggerLabel,
      className = _ref.className,
      onSelection = _ref.onSelection;
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setOpen = _useState2[1];

  var IconComponent = isOpen ? ChevronUpIcon : ChevronDownIcon;
  var trigger = jsx(IconButton, {
    css: _styles.headerButton,
    svgIconComponent: IconComponent,
    onClick: function onClick() {
      return setOpen(true);
    },
    buttonType: "tertiary",
    size: "sm",
    "data-ppui": "true"
  }, triggerLabel);
  return jsx("div", {
    css: _styles.flexContainer,
    className: className,
    "data-ppui-info": "data-table_2.12.0"
  }, "Sort by: ".concat(options[selectedIndex].title), trigger, jsx(ActionMenu, {
    isOpen: isOpen,
    onClose: function handleOnClose() {
      setOpen(false);
    },
    options: options,
    onSelection: function handleSelect(index) {
      setOpen(false);
      onSelection(index);
    },
    "data-ppui": "true"
  }));
};

var _excluded$1 = ["items", "adjustments", "totals", "description", "className", "isLoading", "loadingStateLabel", "emptyTableLabel", "idPrefix"];

function ownKeys$3(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread$3(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$3(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$3(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var renderCellContent = function renderCellContent(data, id, Component) {
  var _data$value;

  var content = (_data$value = data === null || data === void 0 ? void 0 : data.value) !== null && _data$value !== void 0 ? _data$value : data;

  if (Component) {
    var props = _typeof(data) === 'object' ? _objectSpread$3({}, data) : {
      value: content
    };
    return jsx(Component, _extends({
      id: id
    }, props, {
      "data-ppui": "true"
    }), content);
  }

  return content;
};

var SummaryHeadingRow = function SummaryHeadingRow(_ref) {
  var tableContext = _ref.tableContext,
      columns = _ref.columns;

  var subContext = _objectSpread$3(_objectSpread$3({}, tableContext), {}, {
    section: 'summaryHeading'
  });

  var cells = columns.map(function (_ref2, index) {
    var title = _ref2.title,
        mobile = _ref2.mobile,
        width = _ref2.width,
        align = _ref2.align;
    subContext.isFirst = index === 0;
    return jsx(TableHeadingCell, {
      key: index,
      mobile: mobile,
      width: width,
      align: align,
      tableContext: subContext,
      "data-ppui": "true"
    }, title);
  });
  return jsx(TableRow, {
    "data-ppui": "true"
  }, cells);
};

var SummaryItemRows = function SummaryItemRows(_ref3) {
  var tableContext = _ref3.tableContext,
      columns = _ref3.columns,
      rows = _ref3.rows;
  var itemRows = rows.map(function (row, rIndex) {
    var cells = columns.map(function (_ref4, index) {
      var mobile = _ref4.mobile,
          width = _ref4.width,
          align = _ref4.align,
          data = _ref4.data,
          cellAs = _ref4.cellAs;
      return jsx(TableCell, {
        tableContext: tableContext,
        key: index,
        mobile: mobile,
        width: width,
        align: align,
        "data-ppui": "true"
      }, renderCellContent(row[data], row.id, cellAs));
    });
    return jsx(TableRow, {
      key: rIndex,
      tableContext: tableContext,
      "data-ppui": "true"
    }, cells);
  });
  return jsx(React.Fragment, null, itemRows);
};

var SummaryRows = function SummaryRows(_ref5) {
  var tableContext = _ref5.tableContext,
      columns = _ref5.columns,
      rows = _ref5.rows;
  var adjustmentRows = rows.map(function (row, rIndex) {
    var cells = columns.map(function (_ref6, index) {
      var mobile = _ref6.mobile,
          width = _ref6.width,
          align = _ref6.align,
          data = _ref6.data,
          cellAs = _ref6.cellAs,
          span = _ref6.span;
      return jsx(TableCell, {
        tableContext: tableContext,
        span: span,
        key: index,
        mobile: mobile,
        align: align,
        width: width,
        "data-ppui": "true"
      }, renderCellContent(row[data], null, cellAs));
    });
    return jsx(TableRow, {
      key: rIndex,
      tableContext: tableContext,
      "data-ppui": "true"
    }, cells);
  });
  return jsx(React.Fragment, null, adjustmentRows);
};

var SummaryTable = function SummaryTable(_ref7) {
  var items = _ref7.items,
      adjustments = _ref7.adjustments,
      totals = _ref7.totals,
      _ref7$description = _ref7.description,
      description = _ref7$description === void 0 ? 'Table' : _ref7$description,
      className = _ref7.className,
      _ref7$isLoading = _ref7.isLoading,
      isLoading = _ref7$isLoading === void 0 ? false : _ref7$isLoading,
      _ref7$loadingStateLab = _ref7.loadingStateLabel,
      emptyTableLabel = _ref7.emptyTableLabel,
      idPrefix = _ref7.idPrefix,
      rest = _objectWithoutProperties(_ref7, _excluded$1);

  var tokens = useTheme();
  var tableStyles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var tableContext = {
    tableType: 'summary',
    isResponsive: true,
    isSticky: true
  };

  var tableProps = _objectSpread$3(_objectSpread$3({}, rest), {}, {
    tableType: 'summary'
  });

  return jsx("div", {
    css: isLoading && tableStyles.tableLoading,
    className: className,
    "data-ppui-info": "data-table_2.12.0"
  }, jsx(ResponsiveTable, _extends({
    "aria-label": description
  }, tableProps, {
    "data-ppui": "true"
  }), jsx(TableHead, {
    "data-ppui": "true"
  }, jsx(SummaryHeadingRow, {
    tableContext: _objectSpread$3(_objectSpread$3({}, tableContext), {}, {
      section: 'summaryHeading'
    }),
    columns: items.columns,
    "data-ppui": "true"
  })), jsx(TableBody$2, {
    "data-ppui": "true"
  }, jsx(SummaryItemRows, _extends({
    tableContext: _objectSpread$3(_objectSpread$3({}, tableContext), {}, {
      section: 'summaryItem'
    })
  }, items, {
    "data-ppui": "true"
  })), jsx(SummaryRows, _extends({
    tableContext: _objectSpread$3(_objectSpread$3({}, tableContext), {}, {
      section: 'summaryAdjust'
    })
  }, adjustments, {
    "data-ppui": "true"
  }))), jsx(TableFoot, {
    "data-ppui": "true"
  }, jsx(SummaryRows, _extends({
    tableContext: _objectSpread$3(_objectSpread$3({}, tableContext), {}, {
      section: 'summaryTotal'
    })
  }, totals, {
    "data-ppui": "true"
  })))));
};

export { DataTable, DataTableSortMenu, DataTableUtils, SummaryTable, styles as dataTableStyles };